/**
 * Small preferences kept in localStorage, by name, in one place.
 *
 * Where a stored value lives:
 * - Here, in localStorage: anything that must be read synchronously at
 *   start-up (the theme has to be known before the first paint) and UI chrome
 *   that belongs to this browser rather than to the data: theme, palette,
 *   dismissed guides and prompts.
 * - In IndexedDB meta, through usePersistentState: page view state (filters,
 *   sorting, selections) that travels with an exported session.
 *
 * Existing keys stay where they are: moving one would lose what users have
 * already stored under it.
 *
 * Storage can be unavailable, in a private window or by policy, so every
 * access tolerates failure and falls back to the default.
 */
export const PREFERENCE = {
  theme: 'everse-theme',
  contrast: 'everse-contrast',
  accent: 'everse-accent',
  palette: 'everse-palette',
  syncInterval: 'everse-sync-interval',
  registryPromptDismissed: 'everse-registry-prompt-dismissed',
  onboardingChecklist: 'everse-onboarding-checklist',
  hiddenCards: 'everse-hidden-cards',
} as const

/** Not a preference: a record of when sources were last synced. */
export const LAST_SYNC = 'everse-last-sync'

/** Written by earlier versions and no longer read. Cleared on reset. */
const RETIRED = [
  'everse-inspect-sort',
  'everse-inspect-view-mode',
  'overview_guide_dismissed',
  'everse-onboarding-dismissed',
  'everse-onboarding-dont-show-startup',
  'everse-dismissed-guides',
  'everse-compare-project-tip-dismissed',
  'everse-feedback-history',
  'everse-contribute-dismissed',
]

export function readPreference(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

export function writePreference(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    // The value still applies for this visit.
  }
}

/** Every preference back to its default, and nothing else. */
export function clearPreferences(): void {
  for (const key of [...Object.values(PREFERENCE), ...RETIRED]) {
    try {
      localStorage.removeItem(key)
    } catch {
      // Nothing stored, or nothing that can be.
    }
  }
  // Whatever holds a preference in memory reads it again (onPreferencesReset).
  window.dispatchEvent(new Event(PREFERENCES_RESET))
}

const PREFERENCES_RESET = 'everse-preferences-reset'

/** Calls `read` after every reset of preferences; returns the unsubscribe. */
export function onPreferencesReset(read: () => void): () => void {
  window.addEventListener(PREFERENCES_RESET, read)
  return () => window.removeEventListener(PREFERENCES_RESET, read)
}

export function readOnboardingChecklist(): Record<string, boolean> {
  const stored = readPreference(PREFERENCE.onboardingChecklist)
  if (!stored) return {}
  try {
    const parsed: unknown = JSON.parse(stored)
    return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, boolean>) : {}
  } catch {
    return {}
  }
}

export function writeOnboardingChecklist(checklist: Record<string, boolean>): void {
  writePreference(PREFERENCE.onboardingChecklist, JSON.stringify(checklist))
}
